import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const features = [
  {
    icon: '📊',
    title: 'Real-Time Analytics',
    text: 'Monitor your portfolio performance with instant updates and detailed charts.',
  },
  {
    icon: '🎯',
    title: 'Risk Management',
    text: 'Identify exposures, optimize asset allocation, and minimize systematic risks.',
  },
  {
    icon: '💡',
    title: 'Smart Insights',
    text: 'Data-driven recommendations to improve your investment strategy.',
  },
  {
    icon: '📈',
    title: 'Detailed Reports',
    text: 'Generate professional reports for audit, analysis, and results presentation.',
  },
  {
    icon: '🔐',
    title: 'Advanced Security',
    text: 'End-to-end encryption and compliance with international standards.',
  },
  {
    icon: '🌐',
    title: 'Multiple Assets',
    text: 'Stocks, funds, cryptocurrencies, bonds and more. All in one place.',
  },
];

const plans = [
  {
    name: 'Free Trial',
    description: 'Explore the dashboard with no commitment',
    price: '£0',
    period: '14-day free trial',
    cta: 'Start Free Trial',
    features: [
      '14-day free trial',
      '£0 during trial',
      'No credit card required',
      'Basic charts',
      'Manual transaction tracking',
      'Email support',
    ],
  },
  {
    name: 'Professional',
    description: 'For serious investors',
    price: '£5.99',
    period: 'per month',
    cta: 'Choose Professional',
    featured: true,
    features: [
      'Unlimited portfolios',
      'Real-time updates',
      'Advanced analytics',
      'Automatic data import',
      'Custom reports',
      'Alerts & notifications',
      'API access (basic)',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    description: 'For professional fund managers',
    price: 'Custom',
    period: 'Custom pricing',
    cta: 'Contact Us',
    contact: true,
    features: [
      'Everything in Professional',
      'Unlimited users & roles',
      'Custom integrations',
      'Advanced API',
      'Compliance & audit',
      'Dedicated account manager',
      'Guaranteed SLA',
    ],
  },
];

const comparisonRows = [
  ['Price', '£0 trial', '£5.99/mo', 'Custom'],
  ['Trial', '14 days', 'Included', 'Custom'],
  ['Credit card required', '—', '—', '—'],
  ['Portfolios', '5', 'Unlimited', 'Unlimited'],
  ['Update frequency', 'Daily', 'Real-time', 'Real-time'],
  ['Advanced analytics', '—', '✓', '✓'],
  ['Auto import', '—', '✓', '✓'],
  ['Alerts', '—', '✓', '✓'],
  ['API Access', '—', 'Basic', 'Advanced'],
  ['Multiple users', '—', '1 user', 'Unlimited'],
  ['Support', 'Email', 'Priority', 'Dedicated'],
];

const faqs = [
  {
    question: 'Is there a free trial for Professional?',
    answer:
      'Yes, we offer a 14-day free trial without requiring a credit card. Full access to all Professional plan features.',
  },
  {
    question: 'Can I change my plan anytime?',
    answer:
      'Absolutely. You can upgrade, downgrade, or cancel your subscription anytime from your dashboard. Changes apply to the next billing cycle.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'We use end-to-end encryption (AES-256), two-factor authentication, and comply with GDPR and ISO 27001 standards. Your data is never shared with third parties.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept credit cards (Visa, Mastercard, American Express), bank transfers, and PayPal. Enterprise customers can arrange custom billing.',
  },
  {
    question: 'Do you offer 24/7 support?',
    answer:
      "Email support is available to all users. Professional and Enterprise plans get priority support. We're based in UTC timezone and respond within 24 hours.",
  },
  {
    question: 'Can I export my data?',
    answer:
      'Yes, you can download all your data in standard formats (CSV, JSON, Excel) anytime. If you cancel, you have 30 days to download your information.',
  },
];

const footerGroups = [
  {
    title: 'Product',
    links: [
      { label: 'Features', href: '#features' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'Roadmap', href: '#' },
      { label: 'Security', href: '#' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '#' },
      { label: 'Blog', href: '#' },
      { label: 'Careers', href: '#' },
      { label: 'Contact', href: '#' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '#' },
      { label: 'Terms of Service', href: '#' },
      { label: 'Cookie Policy', href: '#' },
      { label: 'Disclaimer', href: '#' },
    ],
  },
  {
    title: 'Connect',
    links: [
      { label: 'Twitter', href: '#' },
      { label: 'LinkedIn', href: '#' },
      { label: 'Discord', href: '#' },
      { label: 'GitHub', href: '#' },
    ],
  },
];

function BrandMark() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <rect x="3" y="15" width="6" height="11" rx="2" fill="#00d4ff" opacity="0.75" />
      <rect x="13" y="9" width="6" height="17" rx="2" fill="#00d4ff" />
      <rect x="23" y="5" width="6" height="21" rx="2" fill="#0099cc" />
      <path d="M5 12 14 7l5 4 8-8" fill="none" stroke="#7ae7ee" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Navbar({ onLogin, onSignup, onAnchorClick }) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`landing-navbar${isScrolled ? ' is-scrolled' : ''}`}>
      <div className="landing-nav-content">
        <div className="landing-logo" aria-label="Northfolio">
          <span className="landing-logo-mark"><BrandMark /></span>
          <span><span className="landing-logo-portfolio">North</span><span className="landing-logo-dashboard">folio</span></span>
        </div>
        <ul className="landing-nav-links">
          <li><a href="#features" onClick={(event) => onAnchorClick(event, 'features')}>Features</a></li>
          <li><a href="#pricing" onClick={(event) => onAnchorClick(event, 'pricing')}>Pricing</a></li>
          <li><a href="#faq" onClick={(event) => onAnchorClick(event, 'faq')}>FAQ</a></li>
        </ul>
        <div className="landing-auth-buttons">
          <button type="button" className="landing-btn-login" onClick={onLogin}>Sign In</button>
          <button type="button" className="landing-btn-signup" onClick={onSignup}>Sign Up</button>
        </div>
      </div>
    </nav>
  );
}

function Hero({ onSignup, onViewPlans }) {
  return (
    <section className="landing-hero">
      <div className="landing-hero-content">
        <h1>Manage your <span className="landing-gradient">investment portfolio</span> with precision</h1>
        <p>
          Real-time analytics, performance tracking, and informed decisions.
          All in an intuitive dashboard designed for professional investors.
        </p>
        <div className="landing-hero-buttons">
          <button type="button" className="landing-btn-primary" onClick={onSignup}>Start Free</button>
          <button type="button" className="landing-btn-secondary" onClick={onViewPlans}>View Plans</button>
        </div>
        <DashboardPreview />
      </div>
    </section>
  );
}

function DashboardPreview() {
  return (
    <div className="landing-dashboard-preview" aria-label="Dashboard preview">
      <div className="landing-preview-header">
        <h2>Portfolio Overview</h2>
        <div className="landing-preview-controls" aria-hidden="true">
          <span className="landing-preview-control">Total P&amp;L <span>⌄</span></span>
          <span className="landing-preview-control">3 months <span>⌄</span></span>
          <span className="landing-preview-icon-button">
            <span></span><span></span><span></span><span></span>
          </span>
        </div>
      </div>

      <div className="landing-preview-metrics">
        <div className="landing-preview-metric">
          <div className="landing-preview-metric-head">
            <span>Total Value</span>
            <span className="landing-preview-metric-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M5 7.5h12.5A2.5 2.5 0 0 1 20 10v7.5A2.5 2.5 0 0 1 17.5 20h-13A2.5 2.5 0 0 1 2 17.5v-11A2.5 2.5 0 0 1 4.5 4H17" />
                <path d="M16 13h5v4h-5a2 2 0 0 1 0-4Z" />
                <path d="M18 15h.01" />
              </svg>
            </span>
          </div>
          <strong>£24,580</strong>
          <p><span>↗ 12.6%</span> vs last 3 months</p>
        </div>
        <div className="landing-preview-metric">
          <div className="landing-preview-metric-head">
            <span>Return</span>
            <span className="landing-preview-metric-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 19V5" />
                <path d="M4 19h16" />
                <path d="m7 15 4-4 3 3 5-6" />
                <path d="M17 8h2v2" />
              </svg>
            </span>
          </div>
          <strong className="is-positive">+8.4%</strong>
          <p><span>↗ 2.1%</span> vs last 3 months</p>
        </div>
        <div className="landing-preview-metric">
          <div className="landing-preview-metric-head">
            <span>Dividends</span>
            <span className="landing-preview-metric-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M20 12v8H4v-8" />
                <path d="M2 7h20v5H2z" />
                <path d="M12 7v13" />
                <path d="M12 7H8.5A2.5 2.5 0 1 1 11 4.5L12 7Z" />
                <path d="M12 7h3.5A2.5 2.5 0 1 0 13 4.5L12 7Z" />
              </svg>
            </span>
          </div>
          <strong>£320</strong>
          <p><span>↗ 5.3%</span> vs last 3 months</p>
        </div>
      </div>

      <div className="landing-preview-chart">
        <svg viewBox="0 0 900 260" role="img" aria-label="Illustrative portfolio growth chart">
          <defs>
            <linearGradient id="landing-chart-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22e66f" stopOpacity="0.25" />
              <stop offset="62%" stopColor="#1bcf78" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#1bcf78" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="landing-chart-line" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#1bdc72" />
              <stop offset="100%" stopColor="#4ff083" />
            </linearGradient>
            <filter id="landing-chart-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <g className="landing-chart-grid">
            <line x1="78" y1="28" x2="870" y2="28" />
            <line x1="78" y1="72" x2="870" y2="72" />
            <line x1="78" y1="116" x2="870" y2="116" />
            <line x1="78" y1="160" x2="870" y2="160" />
            <line x1="78" y1="204" x2="870" y2="204" />
            <line x1="78" y1="28" x2="78" y2="210" />
          </g>

          <path
            className="landing-chart-area"
            d="M78 170 C98 156 110 160 128 144 S166 130 188 142 S226 120 246 108 S290 82 316 98 S356 136 382 118 S420 92 444 116 S482 124 504 104 S548 70 574 88 S618 132 646 146 S690 136 714 106 S750 74 776 68 S812 106 838 76 S856 56 870 44 L870 210 L78 210 Z"
          />
          <path
            className="landing-chart-line"
            d="M78 170 C98 156 110 160 128 144 S166 130 188 142 S226 120 246 108 S290 82 316 98 S356 136 382 118 S420 92 444 116 S482 124 504 104 S548 70 574 88 S618 132 646 146 S690 136 714 106 S750 74 776 68 S812 106 838 76 S856 56 870 44"
          />
          <circle className="landing-chart-point" cx="870" cy="44" r="4.6" />
          <g className="landing-chart-y-labels" aria-hidden="true">
            <text x="0" y="32">£30K</text>
            <text x="0" y="76">£25K</text>
            <text x="0" y="120">£20K</text>
            <text x="0" y="164">£15K</text>
            <text x="0" y="208">£10K</text>
          </g>
          <g className="landing-chart-x-labels" aria-hidden="true">
            <text x="100" y="246">Feb 10</text>
            <text x="240" y="246">Feb 24</text>
            <text x="380" y="246">Mar 10</text>
            <text x="515" y="246">Mar 24</text>
            <text x="650" y="246">Apr 7</text>
            <text x="770" y="246">Apr 21</text>
            <text x="846" y="246">May 5</text>
          </g>
        </svg>
      </div>
    </div>
  );
}

function Features() {
  return (
    <section className="landing-section" id="features">
      <h2 className="landing-section-title">Everything You Need</h2>
      <p className="landing-section-subtitle">Professional tools to manage and optimize your investment portfolio</p>

      <div className="landing-features-grid">
        {features.map((feature) => (
          <div className="landing-feature-card" key={feature.title}>
            <div className="landing-feature-icon" aria-hidden="true">{feature.icon}</div>
            <h3>{feature.title}</h3>
            <p>{feature.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Pricing({ onSignup }) {
  const handlePlanClick = (plan) => {
    if (plan.contact) {
      window.alert('Contact: hello@northfolio.app');
      return;
    }
    onSignup();
  };

  return (
    <section className="landing-section" id="pricing">
      <h2 className="landing-section-title">Simple & Transparent Pricing</h2>
      <p className="landing-section-subtitle">Choose the perfect plan for your needs. No hidden fees, cancel anytime.</p>

      <div className="landing-pricing">
        <div className="landing-pricing-cards">
          {plans.map((plan) => (
            <div className={`landing-pricing-card${plan.featured ? ' featured' : ''}`} key={plan.name}>
              <div className="landing-plan-name">{plan.name}</div>
              <p className="landing-plan-description">{plan.description}</p>
              <div className="landing-plan-price">{plan.price}</div>
              <p className="landing-plan-period">{plan.period}</p>
              <ul className="landing-plan-features">
                {plan.features.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <button type="button" className="landing-plan-cta" onClick={() => handlePlanClick(plan)}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="landing-comparison">
          <table className="landing-comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free Trial</th>
                <th>Professional</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map(([feature, starter, professional, enterprise]) => (
                <tr key={feature}>
                  <td>{feature}</td>
                  {[starter, professional, enterprise].map((value, index) => (
                    <td key={`${feature}-${index}`}>
                      <span className={value === '—' ? 'landing-cross' : value === '✓' ? 'landing-check' : 'landing-table-value'}>{value}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openItems, setOpenItems] = useState([]);

  const toggleFAQ = (index) => {
    setOpenItems((current) => (
      current.includes(index)
        ? current.filter((item) => item !== index)
        : [...current, index]
    ));
  };

  return (
    <section className="landing-section" id="faq">
      <h2 className="landing-section-title">Frequently Asked Questions</h2>
      <p className="landing-section-subtitle">Quick answers to common questions</p>

      <div className="landing-faq-list">
        {faqs.map((faq, index) => {
          const isOpen = openItems.includes(index);
          return (
            <button
              type="button"
              className={`landing-faq-item${isOpen ? ' active' : ''}`}
              onClick={() => toggleFAQ(index)}
              aria-expanded={isOpen}
              key={faq.question}
            >
              <span className="landing-faq-question">
                <span>{faq.question}</span>
                <span className="landing-faq-icon">▼</span>
              </span>
              <span className="landing-faq-answer">{faq.answer}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="landing-section" id="about">
      <h2 className="landing-section-title">Built For Portfolio Clarity</h2>
      <p className="landing-section-subtitle">
        Northfolio brings your holdings, income, trades, cash flow, and performance into one focused view
        so you can understand your portfolio faster.
      </p>
    </section>
  );
}

function CTA({ onSignup }) {
  return (
    <section className="landing-section">
      <div className="landing-cta">
        <h2>Start your free trial today</h2>
        <p>No credit card required. Explore the essentials for 14 days and upgrade when you're ready.</p>
        <button type="button" className="landing-btn-primary landing-cta-button" onClick={onSignup}>
          Create Free Account
        </button>
      </div>
    </section>
  );
}

function Footer({ onAnchorClick }) {
  return (
    <footer className="landing-footer">
      <div className="landing-footer-content">
        {footerGroups.map((group) => (
          <div className="landing-footer-section" key={group.title}>
            <h4>{group.title}</h4>
            <ul>
              {group.links.map((link) => (
                <li key={`${group.title}-${link.label}`}>
                  <a
                    href={link.href}
                    onClick={link.href.startsWith('#') ? (event) => onAnchorClick(event, link.href.slice(1)) : undefined}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="landing-footer-bottom">
        <p>&copy; 2026 Northfolio. All rights reserved.</p>
      </div>
    </footer>
  );
}

function LandingPage() {
  const navigate = useNavigate();

  const handleLogin = () => navigate('/login');
  const handleSignup = () => navigate('/register');

  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;

    const timeout = window.setTimeout(() => {
      scrollToSection(id);
    }, 0);

    return () => window.clearTimeout(timeout);
  }, []);

  const handleAnchorClick = (event, id) => {
    event.preventDefault();
    scrollToSection(id);
  };

  return (
    <main className="landing-page">
      <Navbar onLogin={handleLogin} onSignup={handleSignup} onAnchorClick={handleAnchorClick} />
      <Hero onSignup={handleSignup} onViewPlans={() => scrollToSection('pricing')} />
      <Features />
      <Pricing onSignup={handleSignup} />
      <About />
      <FAQ />
      <CTA onSignup={handleSignup} />
      <Footer onAnchorClick={handleAnchorClick} />
    </main>
  );
}

export default LandingPage;
